import { Component, OnDestroy, OnInit, ViewChild, signal, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { AddressType, GroupLink, TownshipPublicSettings, VoucherGroup } from '../interfaces';
import {
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { getCurrencyString, getTermsUrl, getTownshipImage, updateTheme } from '../globals';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { VerifySmsComponent } from './dialogs/verify-sms/verify-sms.component';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper, MatStep, MatStepperPrevious } from '@angular/material/stepper';
import {
  ActivateVoucherComponentData,
  ActivateVoucherComponent,
} from '../activate-voucher/activate-voucher.component';
import { db, functions } from '../app.component';
import { httpsCallable } from 'firebase/functions';
import { PublicFunctionsService } from '../services/public-functions.service';
import { CustomValidators } from 'angular-custom-validators';
import { NgStyle } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatError, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatRipple } from '@angular/material/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { Subscription } from 'rxjs';

interface GroupLinkExtended extends GroupLink {
  voucherGroups: VoucherGroup[];
}

interface InterimAddressTypeGroups {
  required: AddressType[];
  optional: AddressType[];
}

@Component({
  selector: 'app-find-voucher-groups',
  templateUrl: './find-voucher-groups.component.html',
  styleUrls: ['./find-voucher-groups.component.scss'],
  imports: [
    NgStyle,
    MatButton,
    MatIcon,
    MatStepper,
    MatStep,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatHint,
    MatProgressSpinner,
    MatRipple,
    MatStepperPrevious,
    MatCheckbox,
    ActivateVoucherComponent,
    TranslatePipe,
    MatRadioModule,
  ],
})
export class FindVoucherGroupsComponent implements OnInit, OnDestroy {
  readonly db = db;
  readonly functions = functions;
  loaded = signal(false);
  campaignInactive = signal(false);
  awaitingResponse = signal(false);
  noActions = signal(false);
  nonLinkedSearch = signal(false);
  errorMessage = signal<string>(undefined);
  townshipId = signal<string>(undefined);
  voucherGroupId = signal<string>(undefined);
  township = signal<TownshipPublicSettings>(undefined);
  townshipName = signal<string>(undefined);
  voucherGroups = signal<VoucherGroup[]>(undefined);
  groupLinkedVoucherGroups = signal<VoucherGroup[]>(undefined);
  voucherGroupGroupLinks = signal<any[]>(undefined);
  filteredVoucherGroups = computed(() => {
    const links = this.groupLinksBase() ?? [];
    const linkedVoucherGroups = this.groupLinkedVoucherGroups() ?? [];

    const groupedByLinkName = new Map<string, VoucherGroup[]>();
    for (const voucherGroup of linkedVoucherGroups) {
      const groupLinkName = voucherGroup.groupLink ?? 'none';
      const existing = groupedByLinkName.get(groupLinkName) ?? [];
      existing.push(voucherGroup);
      groupedByLinkName.set(groupLinkName, existing);
    }

    const filtered: VoucherGroup[] = [];
    for (const link of links) {
      const vouchers = groupedByLinkName.get(link.name);
      if (vouchers && vouchers.length > 0) {
        filtered.push(...vouchers);
      }
    }
    return filtered;
  });
  groupLinksFullInfo = signal<any[]>(undefined);
  groupLinksBase = signal<GroupLink[]>(undefined);
  groupLinks = computed(() => {
    const links = this.groupLinksBase() ?? [];
    const linkedVoucherGroups = this.groupLinkedVoucherGroups() ?? [];

    const groupedByLinkName = new Map<string, VoucherGroup[]>();
    for (const voucherGroup of linkedVoucherGroups) {
      const groupLinkName = voucherGroup.groupLink ?? 'none';
      const existing = groupedByLinkName.get(groupLinkName) ?? [];
      existing.push(voucherGroup);
      groupedByLinkName.set(groupLinkName, existing);
    }

    const groupedLinks = links
      .map((groupLink) => {
        const voucherGroups = groupedByLinkName.get(groupLink.name) ?? [];
        return {
          name: `${groupLink.name}`,
          text: groupLink.text,
          order: groupLink.order,
          hideDivider: groupLink.hideDivider,
          voucherGroups,
        } as GroupLinkExtended;
      })
      .filter((groupLink) => groupLink.voucherGroups && groupLink.voucherGroups.length > 0)
      .sort((groupLink1, groupLink2) => {
        if (groupLink1.order > groupLink2.order) {
          return 1;
        }
        if (groupLink1.order < groupLink2.order) {
          return -1;
        }
        if (groupLink1.name > groupLink2.name) {
          return 1;
        }
        if (groupLink1.name < groupLink2.name) {
          return -1;
        }
        return 0;
      });

    return groupedLinks;
  });
  checked = signal(false);
  townshipLogo = signal<string>(undefined);
  termsUrl = signal<string>(getTermsUrl('general'));
  privacyUrl = signal<string>(getTermsUrl('privacy'));
  savedFormInfo = signal<{
    postal: string;
    houseNumber: string;
    houseNumberAddition: string;
  }>(undefined);
  addressString = signal('');
  phonenumber = signal<string>(undefined);
  email = signal<string>(undefined);
  addressForm = new FormGroup({
    postal: new FormControl('', [Validators.required, CustomValidators.postal]),
    houseNumber: new FormControl('', [
      Validators.required,
      CustomValidators.numberInput(false, false, 0),
    ]),
    houseNumberAddition: new FormControl('', [Validators.pattern(/^[a-zA-Z0-9]*$/)]),
  });
  verificationForm: FormGroup = new FormGroup({
    pin: new FormControl(null),
  });
  voucherGroupForm = new FormGroup({
    voucherGroup: new FormControl<VoucherGroup | null>(null, Validators.required),
  });
  termsForm = new FormGroup({
    acceptTerms: new FormControl(null, Validators.requiredTrue),
  });
  interimAddressTypeForm = new FormGroup({});
  townshipImage = signal<any>(undefined);

  highContrast = signal(false);
  pinVerificationNeeded = signal(true);
  phoneVerified = signal(false);
  preparingRequest = signal(false);
  readonly queryParams = toSignal(this.route.queryParams, { initialValue: {} as any });
  addressTypes = computed(() => {
    const kenmerken = this.queryParams()?.kenmerken;
    if (!kenmerken || typeof kenmerken !== 'string') {
      return [];
    }
    return kenmerken
      .split(',')
      .map((item: string) => item.trim())
      .filter((item: string) => item.length > 0);
  });
  infoButtonActive = signal(false);
  selectedInterimAddressTypes = signal<string[]>([]);
  interimAddressTypes = signal<InterimAddressTypeGroups>({ required: [], optional: [] });
  interimAddressStepEnabled = signal(false);

  getCurrencyString = getCurrencyString;
  @ViewChild('stepper') stepper: MatStepper;
  previousStepperIndex = 0;
  activateVoucherComponentData: ActivateVoucherComponentData | null;
  private readonly subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private translate: TranslateService,
    public dialog: MatDialog,
    private publicFunctionsService: PublicFunctionsService,
  ) {}

  ngOnInit(): void {
    this.translate.setFallbackLang(environment.language);
    this.translate.use(environment.language);

    if (localStorage.getItem('highContrast') && localStorage.getItem('highContrast') === '1') {
      this.highContrast.set(true);
    }

    this.subscriptions.add(
      this.route.params.subscribe(async (params) => {
        console.log('params', params);
        this.townshipName.set(params.townshipName);
        if (this.townshipName()) {
          await this.getInfo();
        } else {
          this.nonLinkedSearch.set(true);
        }
        if (params.customParams) {
          const customParamsSplit = params.customParams.split('*');
          if (customParamsSplit.length < 2) {
            // Not an adress param
            if (params.customParams === 'prepare') {
              this.preparingRequest.set(true);
            }
            return;
          }
          const patchObj = {
            postal: customParamsSplit[0],
            houseNumber: customParamsSplit[1],
            houseNumberAddition: customParamsSplit[2],
          };
          this.email.set(customParamsSplit[3]);
          this.addressForm.patchValue(patchObj);
          await this.findVoucherGroups(null, null, null, true, true);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async getInfo() {
    if (!this.townshipId()) {
      const townshipNameDoc = (
        await getDoc(doc(this.db, `townshipNames/${this.townshipName()}`))
      ).data();
      if (!townshipNameDoc.townshipId) {
        return;
      }
      this.townshipId.set(townshipNameDoc.townshipId);
    }
    const townshipData = (
      await getDoc(doc(this.db, `township/${this.townshipId()}/settings/public`))
    ).data() as TownshipPublicSettings;
    this.township.set(townshipData);
    this.townshipName.set(this.township().name);
    this.townshipImage.set(getTownshipImage(this.township()));
    updateTheme({
      primary: this.township().primaryColor,
      secondary: this.township().accentColor,
    });
    if (this.township()?.hideSource) {
      this.campaignInactive.set(true);
      return;
    }
    this.loaded.set(true);
  }

  async findVoucherGroups(
    pin: string | null = null,
    phonenumber: string | null = null,
    verificationCode: string | null = null,
    skipVerification: boolean = false,
    checkInterimAddressTypes: boolean = false,
  ) {
    if (this.addressForm.valid && !this.awaitingResponse()) {
      this.noActions.set(false);
      const form = this.addressForm.value;
      this.awaitingResponse.set(true);
      const postal = (form.postal as string).toUpperCase().replace(/\s+/g, '');
      const houseNumber = form.houseNumber.toString();
      const houseNumberAddition = form.houseNumberAddition
        ? (form.houseNumberAddition as string)
        : null;
      this.addressString.set(postal.concat(' ', houseNumber, houseNumberAddition ?? ''));
      console.log('addressString', this.addressString());
      const callable = httpsCallable(this.functions, 'httpSearchVoucherGroups');
      console.log('calling httpSearchVoucherGroups');
      if (this.nonLinkedSearch()) {
        this.townshipId.set(undefined);
        this.voucherGroups.set(undefined);
      }

      let result: any;
      try {
        const recaptchaToken =
          await this.publicFunctionsService.getRecaptchaToken('search_voucher_groups');
        result = (
          await callable({
            townshipId: this.townshipId(),
            postal,
            houseNumber,
            houseNumberAddition,
            pin,
            phonenumber: phonenumber,
            verificationCode: verificationCode,
            skipVerification: skipVerification,
            checkInterimAddressTypes: checkInterimAddressTypes,
            addressTypes: this.addressTypes(),
            measures: this.selectedInterimAddressTypes(),
            recaptchaToken,
          })
        ).data as any;
        console.log('result', result);
        if (result.townshipId) {
          this.townshipId.set(result.townshipId);
          await this.getInfo();
        }
      } finally {
        this.awaitingResponse.set(false);
      }

      if (
        (this.stepper.selectedIndex === 0 && this.previousStepperIndex !== 0) ||
        (this.interimAddressStepEnabled() &&
          this.stepper.selectedIndex === 1 &&
          this.previousStepperIndex > 1)
      ) {
        this.verificationForm.reset();
        this.pinVerificationNeeded.set(true);
        this.phoneVerified.set(false);
        this.voucherGroups.set([]);
        this.groupLinkedVoucherGroups.set([]);
        this.groupLinksBase.set([]);
        this.voucherGroupGroupLinks.set([]);
        if (this.stepper.selectedIndex === 0) {
          this.interimAddressStepEnabled.set(false);
        }
      }

      if (result.status === 'pin') {
        this.verificationForm.controls.pin.setErrors({
          incorrectPin: true,
        });
        this.verificationForm.markAllAsTouched();
        this.pinVerificationNeeded.set(true);
        return;
      } else if (!skipVerification) {
        this.verificationForm.controls.pin.setErrors(null);
      }
      if (result.status === 'address_not_found_in_system') {
        this.errorMessage.set('find-voucher-group.error_address_not_found');
        return;
      } else if (
        result.status === 'smsVerificationRequired' ||
        result.status === 'verification_code_sent'
      ) {
        this.phoneVerified.set(false);
        return this.verifySms(result.status);
      } else if (result.status === 'phonenumber_does_not_match') {
        this.errorMessage.set('find-voucher-group.error_matching_phonenumber');
        return;
      } else if (result.status === 'phonenumber_already_coupled_to_other_address') {
        this.errorMessage.set('find-voucher-group.error_coupled_phonenumber');
        return;
      } else if (result.status === 'interim_address_types') {
        this.buildInterimAddressTypesForm(result.interimAddressTypes);
        return this.stepper.next();
      } else if (result.status === 'no_valid_voucher_groups_found') {
        this.noActions.set(true);
        this.pinVerificationNeeded.set(false);
        this.voucherGroups.set([]);
        this.errorMessage.set(undefined);
        return this.stepper.next();
      }

      this.groupLinksFullInfo.set([]); // all grouplinks in a township
      const allGroupLinks = await getDocs(
        collection(this.db, `township/${this.townshipId()}/groupLinks`),
      );
      const groupLinksFullInfo = [];
      allGroupLinks.forEach((groupLinkInfoDoc) => {
        const groupLinkInfo = groupLinkInfoDoc.data();
        if (!groupLinkInfo.order) {
          groupLinkInfo.order = 1;
        }
        groupLinksFullInfo.push(groupLinkInfo);
      });
      groupLinksFullInfo.push({
        name: 'none',
        text: 'Overig',
        order: 10000,
        hideDivider: true,
      });
      this.groupLinksFullInfo.set(groupLinksFullInfo);

      if (result.voucherGroups) {
        this.groupLinkedVoucherGroups.set([]); //vouchergroups with a grouplink
        this.groupLinksBase.set([]); // all grouplinks(the objects) in the result of vouchergroups
        this.voucherGroupGroupLinks.set([]); // all grouplinks(only names) in the result of vouchergroups

        const groupLinkedVoucherGroups = [];
        const groupLinks = [];
        const voucherGroupGroupLinks = [];

        result.voucherGroups.forEach((resultVoucherGroup) => {
          if (!resultVoucherGroup.groupLink) {
            resultVoucherGroup.groupLink = 'none';
          }
          groupLinkedVoucherGroups.push(resultVoucherGroup);
          if (!voucherGroupGroupLinks.includes(resultVoucherGroup.groupLink)) {
            this.groupLinksFullInfo().forEach((groupLinksSelectedInfo) => {
              if (groupLinksSelectedInfo.name === resultVoucherGroup.groupLink) {
                groupLinks.push(groupLinksSelectedInfo);
              }
            });
            voucherGroupGroupLinks.push(resultVoucherGroup.groupLink);
          }
        });
        this.groupLinkedVoucherGroups.set(groupLinkedVoucherGroups);
        this.groupLinksBase.set(groupLinks);
        this.voucherGroupGroupLinks.set(voucherGroupGroupLinks);

        if (result.secureGroupsFound) {
          const securedGroups = result.voucherGroups.filter(
            (group) => group.secured === undefined || group.secured === null || group.secured,
          );
          if (securedGroups.length > 0) {
            this.pinVerificationNeeded.set(false);
          }
        } else {
          this.pinVerificationNeeded.set(false);
        }

        this.voucherGroups.set(result.voucherGroups);
        this.savedFormInfo.set({
          postal: postal,
          houseNumber,
          houseNumberAddition,
        });

        this.errorMessage.set(undefined);

        if ((!pin && !verificationCode) || (!pin && this.phoneVerified())) {
          this.stepper.next();
        }
      } else if (result.error === 'errorAddress') {
        this.errorMessage.set('find-voucher-group.error_searching_address');
      } else {
        this.errorMessage.set('find-voucher-group.generic_error');
      }
      this.previousStepperIndex = this.stepper.selectedIndex;
    }
  }

  setInterimAddressTypes() {
    const values: string[] = [];
    Object.entries(this.interimAddressTypeForm.value).forEach(([key, value]) => {
      if (key === 'livingSituation' && value) {
        values.push(value as string);
      } else if (value === true) {
        values.push(key);
      }
    });
    this.selectedInterimAddressTypes.set(values);
    this.findVoucherGroups(null, null, null, true, false);
  }

  buildInterimAddressTypesForm(interimAddressTypes: AddressType[]) {
    const requiredTypes = interimAddressTypes.filter((type) => !type.optional);
    const optionalTypes = interimAddressTypes.filter((type) => type.optional);

    this.interimAddressTypeForm = new FormGroup({});
    if (requiredTypes.length > 0) {
      this.interimAddressTypeForm.addControl(
        'livingSituation',
        new FormControl(
          requiredTypes.length === 1 ? requiredTypes[0].id : null,
          Validators.required,
        ),
      );
    }
    if (optionalTypes.length > 0) {
      optionalTypes.forEach((addressType) => {
        this.interimAddressTypeForm.addControl(addressType.id, new FormControl(false));
      });
    }
    this.interimAddressTypes.set({ required: requiredTypes, optional: optionalTypes });
    this.interimAddressStepEnabled.set(true);
  }

  async generateActivateVoucherStep() {
    if (!this.termsForm.valid) {
      this.termsForm.markAllAsTouched();
      return;
    }
    const addressForm = this.addressForm.value;
    const voucherGroupForm = this.voucherGroupForm.value;
    this.activateVoucherComponentData = {
      townshipId: this.townshipId(),
      voucherGroupId: voucherGroupForm.voucherGroup.id,
      userData: { ...(addressForm as any), email: this.email() ?? '' },
      width: '590px',
      termsAccepted: true,
      noStatistics: true,
      activateType: 'find-voucher-groups',
      preparingRequest: this.preparingRequest(),
    };
    this.stepper.next();
  }

  verifySms(status?: string): Promise<void> {
    let phoneSubmitted = false;
    if (status === 'smsVerificationRequired') {
      phoneSubmitted = false;
    } else if (status === 'verification_code_sent') {
      phoneSubmitted = true;
    }
    const dialogRef = this.dialog.open(VerifySmsComponent, {
      width: '350px',
      data: { phonenumberSubmitted: phoneSubmitted },
      disableClose: true,
    });
    return new Promise<void>((resolve, reject) => {
      dialogRef.afterClosed().subscribe({
        next: async (result) => {
          try {
            if (result) {
              const phonenumber =
                result.phonenumber !== '' ? result.phonenumber : this.phonenumber();
              this.phonenumber.set(phonenumber);
              this.phoneVerified.set(true);
              await this.findVoucherGroups(
                this.verificationForm.value.pin,
                this.phonenumber(),
                result.code,
              );
            }
            resolve();
          } catch (error) {
            reject(error);
          }
        },
        error: (error) => reject(error),
      });
    });
  }

  filterGroupLinks() {
    // kept for backwards compatibility; derived state is now handled with computed signals
    this.groupLinksBase.set([...(this.groupLinksBase() ?? [])]);
  }

  sortGrouplinks() {
    const sorted = [...(this.groupLinksBase() ?? [])].sort((groupLink1, groupLink2) => {
      if (groupLink1.order > groupLink2.order) {
        return 1;
      }
      if (groupLink1.order < groupLink2.order) {
        return -1;
      }

      if (groupLink1.name > groupLink2.name) {
        return 1;
      }
      if (groupLink1.name < groupLink2.name) {
        return -1;
      }
      return 0;
    });
    this.groupLinksBase.set(sorted);
  }

  async selectVoucherGroup(voucherGroup) {
    this.termsUrl.set(getTermsUrl('general', voucherGroup?.termsURL, this.township()?.termsUrl));
    this.voucherGroupForm.controls.voucherGroup.setValue(voucherGroup);
    this.stepper.next();
  }

  openInfoButtonUrl(url: string) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  getError(form: FormGroup, fieldName: string) {
    const field = form.get(fieldName);
    if (field.touched || !field.pristine) {
      let error;
      if (field.hasError('required')) {
        error = 'required';
      }
      if (field.hasError('startsWith')) {
        error = 'must_start_with_06';
      }
      if (field.hasError('email')) {
        error = 'email';
      }
      if (field.hasError('postalValidator')) {
        error = 'pattern';
      }
      if (field.hasError('pattern') && fieldName === 'houseNumberAddition') {
        error = 'alphanumeric';
      }
      if (field.hasError('minlength')) {
        error = 'phone';
      }
      if (field.hasError('incorrectPin')) {
        error = 'incorrect_pin';
      }
      const res = this.translate.instant(`errors.${error}`) as string;
      return res;
    }
    return '';
  }

  setHighContrast() {
    this.highContrast.update((v) => !v);
    localStorage.setItem('highContrast', this.highContrast() ? '1' : '0');
  }
}
